/**
 * Shared source of truth for "trip change reason" events captured into
 * `public.change_events`. Mirrored by the BEFORE-INSERT trigger
 * `public.validate_change_event` — keep both in sync.
 *
 * Derived from the Titus Deprecation Performance Tracking matrix.
 */

export type ChangeEventType =
  | "trip_created_manual"
  | "trip_type_updated"
  | "trip_equipment_trailer"
  | "trip_equipment_tractor"
  | "trip_cancelled"
  | "confirm_docs_override"
  | "pending_bol_enabled";

export type ChangeEventReason =
  | "no_autonomous_truck_available"
  | "staffing"
  | "customer_request"
  | "customer_cancelled"
  | "trailer_quality"
  | "trailer_weight"
  | "no_load_provided"
  | "no_truck_available"
  | "dispatch_error"
  | "inspection_failure"
  | "troubleshooting"
  /** @deprecated Superseded by `troubleshooting`; retained for historical rows. */
  | "unable_to_launch"
  | "fleet_restricted"
  | "autonomy_restricted_in_area"
  | "documentation_override"
  | "autonomy_restricted_on_truck"
  | "weather_out_of_odd"
  | "other";

/** Reason allow-list per event type (mirrors the SQL trigger). */
export const REASONS_BY_EVENT: Record<ChangeEventType, ChangeEventReason[]> = {
  trip_created_manual: [
    "no_autonomous_truck_available",
    "staffing",
    "other",
  ],
  trip_type_updated: [
    "no_autonomous_truck_available",
    "staffing",
    "trailer_weight",
    "inspection_failure",
    "troubleshooting",
    "unable_to_launch",
    "fleet_restricted",
    "autonomy_restricted_in_area",
    "autonomy_restricted_on_truck",
    "weather_out_of_odd",
    "other",
  ],
  trip_equipment_trailer: [
    "customer_request",
    "trailer_quality",
    "trailer_weight",
    "inspection_failure",
    "autonomy_restricted_on_truck",
    "weather_out_of_odd",
    "other",
  ],
  trip_equipment_tractor: [
    "no_autonomous_truck_available",
    "trailer_weight",
    "inspection_failure",
    "troubleshooting",
    "unable_to_launch",
    "fleet_restricted",
    "autonomy_restricted_in_area",
    "autonomy_restricted_on_truck",
    "weather_out_of_odd",
    "other",
  ],
  trip_cancelled: [
    "staffing",
    "customer_cancelled",
    "trailer_quality",
    "trailer_weight",
    "no_load_provided",
    "no_truck_available",
    "dispatch_error",
    "inspection_failure",
    "troubleshooting",
    "unable_to_launch",
    "fleet_restricted",
    "autonomy_restricted_in_area",
    "autonomy_restricted_on_truck",
    "weather_out_of_odd",
    "other",
  ],
  confirm_docs_override: [
    "documentation_override",
    "other",
  ],
  pending_bol_enabled: [
    "documentation_override",
    "other",
  ],
};

export const EVENT_LABELS: Record<ChangeEventType, string> = {
  trip_created_manual: "Trip created (Manual)",
  trip_type_updated: "Trip type updated",
  trip_equipment_trailer: "Trailer pivot",
  trip_equipment_tractor: "Tractor pivot",
  trip_cancelled: "Trip cancelled",
  confirm_docs_override: "Confirmed without required documentation",
  pending_bol_enabled: "Pending BOL enabled",
};

export const REASON_LABELS: Record<ChangeEventReason, string> = {
  no_autonomous_truck_available: "No autonomous truck available",
  staffing: "Staffing",
  customer_request: "Customer request",
  customer_cancelled: "Customer cancelled",
  trailer_quality: "Trailer quality",
  trailer_weight: "Trailer weight",
  no_load_provided: "No load provided",
  no_truck_available: "No truck available",
  dispatch_error: "Dispatch error",
  inspection_failure: "Inspection failure",
  troubleshooting: "Troubleshooting",
  // Deprecated: superseded by `troubleshooting`. Kept so historical
  // `change_events` rows still render; never offered in the UI.
  unable_to_launch: "Troubleshooting",
  fleet_restricted: "Fleet restricted",
  autonomy_restricted_in_area: "Autonomy restricted in area",
  documentation_override: "Admin override — missing billing documentation",
  autonomy_restricted_on_truck: "Autonomy restricted on truck",
  weather_out_of_odd: "Weather out of ODD",
  other: "Other",
};

/**
 * Reasons that may only be set by the system (e.g. API-driven TaaS cancel),
 * never offered in the dispatcher UI.
 */
export const SYSTEM_ONLY_REASONS: Set<ChangeEventReason> = new Set([
  "customer_cancelled",
  "documentation_override",
]);

/**
 * Reasons still valid in the DB but no longer offered in any UI picker.
 */
export const DEPRECATED_REASONS: Set<ChangeEventReason> = new Set([
  "unable_to_launch",
]);

export function userSelectableReasons(eventType: ChangeEventType): ChangeEventReason[] {
  return REASONS_BY_EVENT[eventType].filter(
    (r) => !SYSTEM_ONLY_REASONS.has(r) && !DEPRECATED_REASONS.has(r),
  );
}

export interface ChangeEventInput {
  orderId: string;
  tenantId: string;
  eapiTripId?: string | null;
  eventType: ChangeEventType;
  reason: ChangeEventReason;
  otherText?: string | null;
  previousValue?: unknown;
  newValue?: unknown;
}

/** Local validator mirroring the DB trigger; throws on invalid input. */
export function validateChangeEvent(input: ChangeEventInput): void {
  const allowed = REASONS_BY_EVENT[input.eventType];
  if (!allowed) throw new Error(`Unknown event_type: ${input.eventType}`);
  if (!allowed.includes(input.reason)) {
    throw new Error(
      `Reason "${input.reason}" not allowed for event "${input.eventType}"`,
    );
  }
  if (input.reason === "other" && !input.otherText?.trim()) {
    throw new Error('"Other" reason requires explanatory text');
  }
}